import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { OrderSearchItem } from "@/models/orders";
import { OrderStatus } from "@/models/enums";
import { OrdersFilterRequest } from "@/models/orders";
import { styled } from "@mui/material";
import { ButtonUnstyled, buttonUnstyledClasses } from "@mui/base";
import { formatDate, formatAmount } from "@/util";
import InfiniteScroll from "react-infinite-scroll-component";
import filtersStore from "@/stores/filtersStore";

interface OrdersTableProps {
    setFilter: Function;
    filter: OrdersFilterRequest;
    orders: OrderSearchItem[];
    total: number;
    fetchMore: any;
}

const DetailsBtn = styled(ButtonUnstyled)(
    () => `
          &:hover {
            background-color: #222C4B;
            width: 100%;
          }
        
          &.${buttonUnstyledClasses.active} {
            background-color: #222C4B;
            width: 100%;
          }

          &.${buttonUnstyledClasses.root} {
            width: 100%;
            height: 26px;
            font-size: 14px;
          }
        
          &.${buttonUnstyledClasses.focusVisible} {
            box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
            outline: none;
            width: 100%;
          }
        
          &.${buttonUnstyledClasses.disabled} {
            opacity: 0.5;
            cursor: not-allowed;
            width: 100%;
          }`
);

function OrdersTable(props: OrdersTableProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const setNext = () => {
        filtersStore.setOrdersFilterData(null);
        props.fetchMore();
    };

    function getArrowClass(sort: string) {
        if (sort === props.filter.orderBy) {
            if (props.filter.orderAsc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    function navigateToOrderDetails(order: OrderSearchItem) {
        navigate(`/orders/${order.id}`, { state: { docketNumber: order.docket } });
    }

    const sortBy = (sortBy: string) => {
        filtersStore.setOrdersFilterData(null);
        if (props.filter.orderBy !== sortBy) {
            props.setFilter({
                ...props.filter,
                orderAsc: true,
                orderBy: sortBy,
                pageIndex: 0,
            });
        } else {
            props.setFilter({
                ...props.filter,
                orderAsc: !props.filter.orderAsc,
                orderBy: sortBy,
                pageIndex: 0,
            });
        }
    };

    const orderStatusClassName = (order: OrderSearchItem) => {
        return order.status.toString() === OrderStatus[OrderStatus.OK]
            ? "status-color-active"
            : order.status.toString() === OrderStatus[OrderStatus.CN]
                ? "status-color-cancel"
                : "status-color-inactive";
    };

    // property order.subsidyAmount - come converted to order currency rate
    // CurrencyRate new field, by default 1 (rate on NIS) even order currency != NIS
    // subsidyAmount computed - need to show correct amount with correct currency
    const subsidyAmount = (order: OrderSearchItem) => {
        return order.currencyCode !== 'NIS' && order.currencyRate > 1 ?
            formatAmount(order.subsidyAmount, order.currencyId) :
            formatAmount(order.subsidyAmount, 1);
    };

    return (
        <div id="orders-list-grid" className="employee-list-grid">
            <InfiniteScroll
                dataLength={props.orders?.length}
                next={setNext}
                hasMore={props.orders?.length < props.total}
                loader={false}
                scrollableTarget="orders-list-grid"
                style={{ overflow: "inherit" }}
            >
                <div className="fake-thead"></div>
                <table>
                    <thead id="orders-table-header">
                        <tr>
                            <td>{t("orders.externalSystem")}</td>
                            <td className="cursor-pointer" onClick={() => sortBy("OrderNumber")}>
                                {t("orders.orderNumber")}
                                <i className={`icon-f ${getArrowClass("OrderNumber")} fs-12`} />
                            </td>
                            <td className="cursor-pointer" onClick={() => sortBy("OrderStatus")}>
                                {t("orders.orderStatus")}
                                <i className={`icon-f ${getArrowClass("OrderStatus")} fs-12`} />
                            </td>
                            <td className="cursor-pointer" onClick={() => sortBy("OrderType")}>
                                {t("orders.orderType")}
                                <i className={`icon-f ${getArrowClass("OrderType")} fs-12`} />
                            </td>
                            <td className="cursor-pointer" onClick={() => sortBy("CreationDate")}>
                                {t("orders.creationDate")}
                                <i className={`icon-f ${getArrowClass("CreationDate")} fs-12`} />
                            </td>
                            <td width={110}>
                                <div className="table-number-format-name">{t("orders.orderTotal")}</div>
                            </td>
                            <td style={{ width: "14%" }} className="cursor-pointer" onClick={() => sortBy("Provider")}>
                                {t("orders.serviceProvider")}
                                <i className={`icon-f ${getArrowClass("Provider")} fs-12`} />
                            </td>
                            <td className="cursor-pointer" onClick={() => sortBy("EmployeeName")}>
                                <span>
                                    {t("orders.employeeName")}
                                    <i className={`icon-f ${getArrowClass("EmployeeName")} fs-12`} />
                                </span>
                            </td>
                            <td>
                                <div className="table-number-format-name">{t("employee.IdCard")}</div>
                            </td>
                            <td>
                                <div className="table-number-format-name">{t("order.general.subsidizedAmount")}</div>
                            </td>
                            <td>
                                <div className="table-number-format-name">{t("orders.paidByEmployee")}</div>
                            </td>
                            <td>{t("orders.details")}</td>
                        </tr>
                    </thead>
                    <tbody>
                        {props.orders?.map((order) => (
                            <tr key={order.id}>
                                <td>{order.externalSystem}</td>
                                <td>
                                    <div className="table-number-format-value">{order.docket}</div>
                                </td>
                                <td>
                                    <div className="order-status">
                                        <div
                                            className={orderStatusClassName(order)}
                                        ></div>
                                        <div>{order.status}</div>
                                    </div>
                                </td>
                                <td>{t(`enums.OrderType.${order.type}`)}</td>
                                <td>
                                    <div className="table-number-format-value">{formatDate(order.createdAt)}</div>
                                </td>
                                <td>
                                    <div className="table-number-format-value">
                                        {formatAmount(order.total, order.currencyId)}
                                    </div>
                                </td>
                                <td>{order.provider}</td>
                                <td>{order.employeeName}</td>
                                <td>
                                    <div className="table-number-format-value">{order.employeePid}</div>
                                </td>
                                <td>
                                    <div className="table-number-format-value">
                                        {subsidyAmount(order)}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-number-format-value">
                                        {formatAmount(order.paidByEmployee, order.currencyId)}
                                    </div>
                                </td>

                                <td>
                                    <DetailsBtn className="basic-button" onClick={() => navigateToOrderDetails(order)}>
                                        {t("orders.button.details")}
                                    </DetailsBtn>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </InfiniteScroll>
        </div>
    );
}

export default OrdersTable;
